import { makeStyles } from "@material-ui/core";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import {
  usePlByProgramStyles,
  PlPageHeader,
} from "./pl-by-program/plByProgramTheme";
import {
  calcFilterPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "./waterfall-eac/filterPopoverUtils";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "./waterfall-eac/waterfallTableUtils";
import {
  getVersionsAPI,
  getContractNotesAllProjectsAPI,
  getContractNotes22402API,
  downloadContractNotesAllProjectsAPI,
  downloadContractNotes22402API,
} from "../../../api/reports-api";
import Loading from "../../../common-components/Loading";

import {
  TH_BG,
  TH_BG_BAND,
} from "../reportTableConfig";

const TAB_ACTIVE = "#4F46E5";
const BORDER = "#E5E7EB";
const FONT = REPORT_FONT;

const TABS = [
  { key: "contract_notes", label: "Contract Notes" },
  { key: "contract_notes_22402", label: "Contract Notes-22402" },
];

const TAB_FILTERS = {
  contract_notes: ["year"],
  contract_notes_22402: ["year"],
};

/* Per-tab download endpoint + output filename */
const DOWNLOAD_BY_TAB = {
  contract_notes: { api: downloadContractNotesAllProjectsAPI, filename: "contract-notes-all-projects.xlsx" },
  contract_notes_22402: { api: downloadContractNotes22402API, filename: "contract-notes-22402.xlsx" },
};

const SORTABLE_COL_KEYS = new Set([
  "bu",
  "forecastVersion",
  "projectNumber",
  "pm",
]);

const HTML_NOTE_KEYS = [
  "headwinds",
  "tailwinds",
  "whatIsInForecast",
  "varianceToBudget",
  "varianceToForecast",
  "notes",
];
const HTML_NOTE_KEY_SET = new Set(HTML_NOTE_KEYS);
const NOWRAP_COL_KEYS = new Set([
  "bu",
  "forecastVersion",
  "projectNumber",
  "pm",
]);

/** Convert API HTML note fields to readable plain text. */
function htmlToPlainText(value) {
  if (value == null || value === "") return "";
  const str = String(value);
  if (!/[<>]|&#|&[a-z]+;/i.test(str)) return str;
  const withBreaks = str
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/\s*p\s*>/gi, "\n")
    .replace(/<\s*\/\s*div\s*>/gi, "\n")
    .replace(/<\s*\/\s*li\s*>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, "• ");
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    const text = doc.body?.textContent || "";
    return text
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  return withBreaks
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function mapContractNotesRow(r) {
  const row = {
    bu: r.bu ?? "",
    forecastVersion: r.forecastVersion ?? "",
    projectNumber: r.projectNumber ?? "",
    pm: r.pm ?? "",
    headwinds: r.headwinds ?? "",
    tailwinds: r.tailwinds ?? "",
    whatIsInForecast: r.whatsInForecast ?? "",
    varianceToBudget: r.varToBudget ?? "",
    varianceToForecast: r.varToForecast ?? "",
    notes: r.notes ?? "",
  };
  HTML_NOTE_KEYS.forEach((key) => {
    row[key] = htmlToPlainText(row[key]);
  });
  return row;
}

function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = (vKeys || FILTERS_CONFIG.map((f) => f.key)).filter(
    (k) => k !== "year",
  );
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === String(new Date().getFullYear())
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  page: {
    fontFamily: FONT,
    backgroundColor: "#F4F5F7",
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 700,
    borderBottom: `2px solid ${TAB_ACTIVE}`,
  },
  headerContainer: { margin: "20px 0 0", padding: "0 16px", flexShrink: 0 },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: { flex: 1, padding: "16px", overflowY: "auto", minWidth: 0 },

  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT,
    "&:hover": { background: "#111827" },
  },

  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
function FilterFunnelIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
function ColumnsIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="18" rx="1" />
      <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
  );
}

/* ── Horizontal filter bar ─────────────────────────────────── */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;
  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };
  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];
        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>
            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}
      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>
      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Shared table style tokens                                     */
/* ══════════════════════════════════════════════════════════════ */
const TH_SH = "inset 0 -1px 0 rgba(255,255,255,.12)";
const TH_SH_G = "inset 0 -2px 0 rgba(255,255,255,.22)";

const thStyle = (isLast, isNumeric) => ({
  background: TH_BG,
  color: isNumeric ? "rgba(255,255,255,.75)" : "rgba(255,255,255,.9)",
  padding: "0 10px",
  height: 34,
  fontWeight: 700,
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: isNumeric ? "right" : "left",
  whiteSpace: "nowrap",
  border: "none",
  boxShadow: isLast ? TH_SH_G : TH_SH,
  position: "sticky",
  top: 0,
  zIndex: 2,
  verticalAlign: "middle",
});

const tdStyle = (bg, isNumeric, isNeg, truncate) => ({
  padding: "7px 10px",
  background: bg,
  color: isNeg ? "#DC2626" : "#334155",
  borderBottom: "1px solid #E2E8F0",
  fontSize: 12,
  verticalAlign: "middle",
  textAlign: isNumeric ? "right" : "left",
  fontVariantNumeric: isNumeric ? "tabular-nums" : undefined,
  ...(truncate
    ? {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 260,
      }
    : { whiteSpace: "pre-wrap", wordBreak: "break-word" }),
});

const NOTE_CELL_STYLE = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  overflow: "visible",
  textOverflow: "clip",
  lineHeight: 1.4,
};

/**
 * Dynamic column size from content.
 * Note columns widen so full text fits in at most ~3 wrapped lines (no ellipsis).
 */
const columnSize = (col, rows = []) => {
  const labelLen = String(col.label || col.key).length;
  let maxLen = labelLen;
  for (const row of rows) {
    const text = String(row?.[col.key] ?? "").replace(/\s+/g, " ").trim();
    if (text.length > maxLen) maxLen = text.length;
  }
  const isNote = HTML_NOTE_KEY_SET.has(col.key);
  if (isNote) {
    // Width = content / 3 so wrapping stays within ~3 lines while showing all text.
    const chars = Math.max(Math.ceil(maxLen / 3), 28);
    return {
      width: `${chars}ch`,
      minWidth: `${chars}ch`,
      maxWidth: `${chars}ch`,
    };
  }
  if (NOWRAP_COL_KEYS.has(col.key)) {
    const chars = Math.max(maxLen + 2, 10);
    return { width: `${chars}ch`, minWidth: `${chars}ch` };
  }
  const chars = Math.min(Math.max(maxLen, 10), 28);
  return { width: `${chars}ch`, minWidth: `${chars}ch` };
};

const filterIconBtn = (active) => ({
  flexShrink: 0,
  width: 16,
  height: 16,
  background: active ? "rgba(165,180,252,0.18)" : "transparent",
  cursor: "pointer",
  padding: 0,
  border: "none",
  borderRadius: 3,
  color: active ? "#A5B4FC" : "rgba(255,255,255,0.5)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  verticalAlign: "middle",
});

const filterPanelStyle = {
  position: "fixed",
  zIndex: 99,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: "10px 12px",
  minWidth: 190,
  fontFamily: FONT,
};
const filterWrap = {
  display: "flex",
  alignItems: "center",
  gap: 3,
  height: 26,
};
const filterTextInput = {
  flex: 1,
  height: 26,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  fontSize: 11,
  padding: "0 7px",
  outline: "none",
  fontFamily: FONT,
  background: "#fff",
  color: "#111827",
};
const filterNumInput = {
  flex: 1,
  height: 26,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  fontSize: 11,
  padding: "0 5px",
  outline: "none",
  fontFamily: FONT,
  background: "#fff",
  color: "#111827",
  textAlign: "right",
};
const opBtn = (active) => ({
  flexShrink: 0,
  width: 22,
  height: 26,
  border: `1.5px solid ${active ? "#4F46E5" : "#CBD5E1"}`,
  borderRadius: 5,
  background: active ? "#4F46E5" : "#fff",
  fontSize: 11,
  fontWeight: 700,
  color: active ? "#fff" : "#4F46E5",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  fontFamily: FONT,
});
const clearBtn = {
  flexShrink: 0,
  width: 18,
  height: 18,
  border: "none",
  borderRadius: "50%",
  background: "#E2E8F0",
  fontSize: 9,
  color: "#64748B",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};
const opDropdownStyle = {
  position: "fixed",
  zIndex: 99,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  minWidth: 190,
  overflow: "hidden",
  fontFamily: FONT,
};

const OP_LABELS = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=", neq: "≠" };
const OP_ENTRIES = [
  { key: "gt", label: "> Greater than" },
  { key: "gte", label: "≥ Greater than or equal" },
  { key: "lt", label: "< Less than" },
  { key: "lte", label: "≤ Less than or equal" },
  { key: "eq", label: "= Equals" },
  { key: "neq", label: "≠ Not equal" },
];

const paginationBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA",
  flexShrink: 0,
  gap: 12,
  flexWrap: "wrap",
};
const paginationInfo = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const paginationRight = { display: "flex", alignItems: "center", gap: 8 };
const paginationControls = { display: "flex", alignItems: "center", gap: 3 };
const pageSizeLabel = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const pageSizeSelect = {
  height: 28,
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 12,
  color: "#374151",
  padding: "0 6px",
  background: "#fff",
  cursor: "pointer",
  fontFamily: FONT,
};
const pgBtn = (disabled) => ({
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  background: "#fff",
  color: disabled ? "#94A3B8" : "#374151",
  fontSize: 12,
  fontWeight: 500,
  cursor: disabled ? "default" : "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
  opacity: disabled ? 0.35 : 1,
});
const pgBtnActive = {
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: `1px solid ${TH_BG_BAND}`,
  borderRadius: 6,
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
};
const pgDots = {
  fontSize: 12,
  color: "#6B7280",
  padding: "0 4px",
  userSelect: "none",
};

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  overflow: "visible",
  display: "flex",
  flexDirection: "column",
  marginBottom: 20,
};
const HEAD = {
  padding: "16px 20px 12px",
  borderBottom: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  position: "relative",
  zIndex: 20,
  overflow: "visible",
  flexShrink: 0,
  background: "#fff",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
};
const CARD_BODY = {
  overflow: "visible",
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
};
const COL_PICKER_DROP = {
  position: "absolute",
  top: "calc(100% + 4px)",
  right: 0,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: "8px 0",
  minWidth: 210,
  zIndex: 1200,
  maxHeight: 420,
  overflowY: "auto",
  fontFamily: FONT,
};
const TITLE_S = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
  letterSpacing: "-0.01em",
  fontFamily: FONT,
  margin: 0,
};

function fmtN(v) {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  if (n === 0) return "$0.00";
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

function applyNumFilter(rawVal, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const v = rawVal == null ? 0 : Number(rawVal);
  const fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":
      return v > fv;
    case "gte":
      return v >= fv;
    case "lt":
      return v < fv;
    case "lte":
      return v <= fv;
    case "eq":
      return v === fv;
    case "neq":
      return v !== fv;
    default:
      return true;
  }
}

/* ══════════════════════════════════════════════════════════════ */
/*  Tab 1 — Contract Notes                                        */
/* ══════════════════════════════════════════════════════════════ */
const COLS_TAB1 = [
  { key: "bu", label: "BU", isText: true },
  {
    key: "forecastVersion",
    label: "Forecast Version",
    isText: true,
  },
  { key: "projectNumber", label: "Project Number", isText: true },
  { key: "pm", label: "PM", isText: true },
  { key: "headwinds", label: "Headwinds", isText: true },
  { key: "tailwinds", label: "Tailwinds", isText: true },
  {
    key: "whatIsInForecast",
    label: "What is in Forecast",
    isText: true,
  },
  {
    key: "varianceToBudget",
    label: "Variance to Budget",
    isText: true,
  },
  {
    key: "varianceToForecast",
    label: "Variance to Forecast",
    isText: true,
  },
  { key: "notes", label: "Notes", isText: true },
];

const ROWS_TAB1 = [
  {
    bu: "Federal Law Enforcement",
    forecastVersion: "Live EAC",
    projectNumber: "01156.000.0002",
    pm: "MCCLELLAN, JOHN",
    headwinds:
      "The Q2 lock has 1 FTE starting in May 2026. Due to the PM being on PTO, I was unable to get the forecast updated prior to lock.",
    tailwinds: "",
    whatIsInForecast: "1 FTE labor through Q4 FY26.",
    varianceToBudget: "$28,000",
    varianceToForecast: "$6,500",
    notes: "",
  },
  {
    bu: "Dept of Homeland Security",
    forecastVersion: "Live EAC",
    projectNumber: "01122.OY3.001",
    pm: "Olson, Scott D",
    headwinds:
      "The Q2 lock is up to date as of the info known on 4/24/26. We only know that the 5 CMSS FTEs are charging OH through 5/1. We are awaiting word on what comes after.",
    tailwinds: "",
    whatIsInForecast: "5 CMSS FTEs; OH through 5/1, scope TBD after.",
    varianceToBudget: "($15,000)",
    varianceToForecast: "($3,200)",
    notes: "Awaiting contracting guidance on post-5/1 scope.",
  },
  {
    bu: "Citizen Services",
    forecastVersion: "Live EAC",
    projectNumber: "01115",
    pm: "RICKERSON, DANIEL",
    headwinds: "",
    tailwinds: "",
    whatIsInForecast: "3 FTE labor plus travel.",
    varianceToBudget: "",
    varianceToForecast: "",
    notes: "",
  },
  {
    bu: "Army",
    forecastVersion: "Live EAC",
    projectNumber: "29842",
    pm: "Aden, Stephen T",
    headwinds: "",
    tailwinds: "",
    whatIsInForecast: "4 FTE integration labor; no ODCs.",
    varianceToBudget: "$9,200",
    varianceToForecast: "$1,800",
    notes: "",
  },
  {
    bu: "Air Force",
    forecastVersion: "Live EAC",
    projectNumber: "21803",
    pm: "TURNER, JENNIFER",
    headwinds: "",
    tailwinds: "",
    whatIsInForecast: "2 FTE advisory support through end of base period.",
    varianceToBudget: "($5,400)",
    varianceToForecast: "($1,100)",
    notes: "",
  },
  {
    bu: "Navy",
    forecastVersion: "Live EAC",
    projectNumber: "22100.001",
    pm: "HARRIS, MICHAEL",
    headwinds:
      "Contract modification pending approval from contracting officer.",
    tailwinds: "Additional task order expected Q3.",
    whatIsInForecast: "3 FTE labor plus travel through end of base period.",
    varianceToBudget: "$9,800",
    varianceToForecast: "$2,100",
    notes: "Base period ends September 2026.",
  },
  {
    bu: "Cyber",
    forecastVersion: "Live EAC",
    projectNumber: "30011",
    pm: "PATEL, PRIYA",
    headwinds: "",
    tailwinds:
      "New subcontract award increases labor coverage through year-end.",
    whatIsInForecast: "5 FTE cybersecurity labor; cloud infrastructure ODCs.",
    varianceToBudget: "$27,500",
    varianceToForecast: "$11,300",
    notes: "ATO received. System deployment on schedule for Q3.",
  },
  {
    bu: "Intelligence",
    forecastVersion: "Live EAC",
    projectNumber: "44502.A",
    pm: "NGUYEN, THOMAS",
    headwinds: "Two key staff departures impacting Q2 delivery timeline.",
    tailwinds: "",
    whatIsInForecast: "4 FTE analytic support; no travel planned.",
    varianceToBudget: "($33,000)",
    varianceToForecast: "($7,500)",
    notes: "Backfill recruiting in progress.",
  },
  {
    bu: "Federal Civilian",
    forecastVersion: "Live EAC",
    projectNumber: "01130.002",
    pm: "DAVIS, CAROL",
    headwinds: "Continuing resolution limits new award activity.",
    tailwinds: "Customer confirmed scope extension through FY26.",
    whatIsInForecast: "6 FTE program support through FY26.",
    varianceToBudget: "$14,200",
    varianceToForecast: "$3,400",
    notes: "Option period exercise confirmed.",
  },
  {
    bu: "Space Force",
    forecastVersion: "Live EAC",
    projectNumber: "55012",
    pm: "ROBINSON, JAMES",
    headwinds: "",
    tailwinds: "",
    whatIsInForecast: "2 FTE space domain awareness support.",
    varianceToBudget: "",
    varianceToForecast: "",
    notes: "",
  },
  {
    bu: "Federal Law Enforcement",
    forecastVersion: "Q2",
    projectNumber: "22402.001",
    pm: "WALKER, AMANDA",
    headwinds: "PM on leave through end of May.",
    tailwinds: "Strong performance metrics support follow-on.",
    whatIsInForecast: "8 FTE labor through Q4 FY26; travel and ODCs included.",
    varianceToBudget: "$45,000",
    varianceToForecast: "$12,000",
    notes: "Option year exercise expected by July 2026.",
  },
  {
    bu: "Army",
    forecastVersion: "Q2",
    projectNumber: "22402.002",
    pm: "BROOKS, KEVIN",
    headwinds: "",
    tailwinds: "Option year exercise expected.",
    whatIsInForecast: "6 FTE through Q3; subcontract labor added in Q4.",
    varianceToBudget: "($18,500)",
    varianceToForecast: "$5,200",
    notes: "Subcontract negotiation finalized.",
  },
  {
    bu: "Dept of Homeland Security",
    forecastVersion: "Q2",
    projectNumber: "22402.003",
    pm: "FLORES, MARIA",
    headwinds: "Technical refresh delayed by supply chain issues.",
    tailwinds: "",
    whatIsInForecast: "Hardware procurement and 4 FTE integration labor.",
    varianceToBudget: "($62,000)",
    varianceToForecast: "($8,400)",
    notes: "Mitigation plan submitted to contracting.",
  },
];

function ContractNotesTab({ filters, version }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  const [textFilters, setTextFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKeys, setSortKeys] = useState([]);

  useEffect(() => {
    setLoading(true);
    getContractNotesAllProjectsAPI(filters, version)
      .then((res) => {
        const apiRows = (res?.data?.rows || []).map(mapContractNotesRow);
        setRows(apiRows);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [JSON.stringify(filters), version]); // eslint-disable-line react-hooks/exhaustive-deps

  const visCols = COLS_TAB1.filter((c) => !hiddenCols.has(c.key));

  useEffect(() => {
    if (!openFilterKey && !colPickerOpen) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]")
      )
        setOpenFilterKey(null);
      if (colPickerRef.current && !colPickerRef.current.contains(e.target))
        setColPickerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey, colPickerOpen]);

  useEffect(() => {
    setPage(1);
  }, [textFilters]);

  const isTextActive = (key) => !!(textFilters[key] && textFilters[key].trim());

  const toggleFilter = useCallback(
    (key) => {
      if (openFilterKey === key) {
        setOpenFilterKey(null);
        return;
      }
      const btn = filterBtnRefs.current[key];
      if (btn)
        setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      setOpenFilterKey(key);
    },
    [openFilterKey],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = rows.filter((row) => {
    for (const col of COLS_TAB1) {
      if (HTML_NOTE_KEY_SET.has(col.key)) continue;
      const v = textFilters[col.key];
      if (
        v &&
        v.trim() &&
        !String(row[col.key] ?? "")
          .toLowerCase()
          .includes(v.trim().toLowerCase())
      )
        return false;
    }
    return true;
  });

  const getSortColumnLabel = useCallback(
    (key) => COLS_TAB1.find((c) => c.key === key)?.label || key,
    [],
  );
  const getSortValue = useCallback((row, key) => row[key] ?? "", []);
  const handleSort = useCallback((colKey) => {
    if (!SORTABLE_COL_KEYS.has(colKey)) return;
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);
  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const totalFiltered = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = sortedRows.slice(start, start + pageSize);
  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  if (loading) return <Loading />;

  return (
    <>
      <div style={CARD}>
        <div style={HEAD}>
          <h2 style={TITLE_S}>Contract Notes — All Projects</h2>
          <div style={{ position: "relative" }} ref={colPickerRef}>
            <button
              type="button"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                height: 28,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #E5E7EB",
                background: colPickerOpen ? "#F0F4FF" : "#fff",
                color: "#374151",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: FONT,
              }}
              onClick={() => setColPickerOpen((p) => !p)}
            >
              <ColumnsIcon /> Columns
            </button>
            {colPickerOpen && (
              <div style={COL_PICKER_DROP}>
                {COLS_TAB1.map((col) => {
                  const hidden = hiddenCols.has(col.key);
                  return (
                    <label
                      key={col.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 14px",
                        cursor: "pointer",
                        fontSize: 12,
                        color: "#374151",
                        background: "none",
                        userSelect: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#F8F9FF";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "none";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!hidden}
                        style={{
                          accentColor: "#4F46E5",
                          width: 13,
                          height: 13,
                        }}
                        onChange={() =>
                          setHiddenCols((prev) => {
                            const next = new Set(prev);
                            if (hidden) next.delete(col.key);
                            else next.add(col.key);
                            return next;
                          })
                        }
                      />
                      {col.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={CARD_BODY}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                borderCollapse: "collapse",
                borderSpacing: 0,
                fontFamily: FONT,
                fontSize: 12,
                width: "max-content",
                minWidth: "100%",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr style={{ height: 34 }}>
                  {visCols.map((col, ci) => {
                    const sortable = SORTABLE_COL_KEYS.has(col.key);
                    return (
                      <th
                        key={col.key}
                        style={{
                          ...thStyle(ci === visCols.length - 1, false),
                          ...columnSize(col, filteredRows),
                          ...(NOWRAP_COL_KEYS.has(col.key)
                            ? { whiteSpace: "nowrap" }
                            : null),
                          cursor: sortable ? "pointer" : undefined,
                        }}
                        onClick={sortable ? onThClick(col.key) : undefined}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {sortable ? (
                            <WaterfallThLabel
                              label={col.label}
                              colKey={col.key}
                              sortKeys={sortKeys}
                              getLabel={getSortColumnLabel}
                              highlight={openFilterKey === col.key}
                            />
                          ) : (
                            <span
                              style={{
                                color:
                                  openFilterKey === col.key
                                    ? "#60A5FA"
                                    : undefined,
                              }}
                            >
                              {col.label}
                            </span>
                          )}
                          {!HTML_NOTE_KEY_SET.has(col.key) && (
                            <button
                              ref={(el) => {
                                filterBtnRefs.current[col.key] = el;
                              }}
                              data-filter-btn
                              style={filterIconBtn(
                                isTextActive(col.key) ||
                                  openFilterKey === col.key,
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFilter(col.key);
                              }}
                              title="Filter"
                            >
                              <FilterFunnelIcon />
                            </button>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => {
                  const bg = i % 2 === 0 ? "#FFFFFF" : "#F7FAFC";
                  return (
                    <tr key={`${row.projectNumber}-${i}`}>
                      {visCols.map((col) => {
                        const v = row[col.key];
                        const isNoteCol = HTML_NOTE_KEY_SET.has(col.key);
                        return (
                          <td
                            key={col.key}
                            style={{
                              ...tdStyle(bg, false, false, false),
                              ...columnSize(col, filteredRows),
                              verticalAlign: isNoteCol ? "top" : "middle",
                              ...(isNoteCol
                                ? { overflow: "visible", whiteSpace: "normal" }
                                : NOWRAP_COL_KEYS.has(col.key)
                                  ? {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                    }
                                  : null),
                            }}
                          >
                            {col.key === "bu" ? (
                              <span
                                style={{ fontWeight: 600, color: "#111827" }}
                              >
                                {v}
                              </span>
                            ) : isNoteCol ? (
                              <div style={NOTE_CELL_STYLE}>{v}</div>
                            ) : (
                              v
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={visCols.length}
                      style={{
                        padding: "24px 20px",
                        textAlign: "center",
                        color: "#9CA3AF",
                        fontSize: 13,
                        fontFamily: FONT,
                      }}
                    >
                      No rows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={paginationBar}>
            <span style={paginationInfo}>
              {totalFiltered === 0
                ? "No rows match the current filters"
                : `${start + 1}–${Math.min(start + pageSize, totalFiltered)} of ${totalFiltered} rows`}
            </span>
            <div style={paginationRight}>
              <span style={pageSizeLabel}>Rows Per Page</span>
              <select
                style={pageSizeSelect}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <div style={paginationControls}>
                <button
                  style={pgBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage(1)}
                  title="First"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M9 10L6 7l3-3M5 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pgBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => p - 1)}
                  title="Previous"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M8.5 10.5L5 7l3.5-3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {pageNumbers.map((p, i) => (
                  <React.Fragment key={p}>
                    {i > 0 && pageNumbers[i - 1] < p - 1 && (
                      <span style={pgDots}>…</span>
                    )}
                    <button
                      style={p === safePage ? pgBtnActive : pgBtn(false)}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
                <button
                  style={pgBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  title="Next"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5.5 3.5L9 7l-3.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pgBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Last"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5 10l3-3-3-3M9 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{
              ...filterPanelStyle,
              top: filterPanelPos.top,
              left: filterPanelPos.left,
            }}
          >
            <div style={filterWrap}>
              <input
                autoFocus
                type="text"
                style={filterTextInput}
                placeholder="Search…"
                value={textFilters[openFilterKey] || ""}
                onChange={(e) =>
                  setTextFilters((p) => ({
                    ...p,
                    [openFilterKey]: e.target.value,
                  }))
                }
              />
              {textFilters[openFilterKey] && (
                <button
                  style={clearBtn}
                  onClick={() =>
                    setTextFilters((p) => {
                      const n = { ...p };
                      delete n[openFilterKey];
                      return n;
                    })
                  }
                >
                  ✕
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Tab 2 — Contract Notes-22402                                  */
/* ══════════════════════════════════════════════════════════════ */
const COLS_TAB2 = [
  { key: "bu", label: "BU", isText: true },
  {
    key: "forecastVersion",
    label: "Forecast Version",
    isText: true,
  },
  { key: "projectNumber", label: "Project Number", isText: true },
  { key: "pm", label: "PM", isText: true },
  { key: "headwinds", label: "Headwinds", isText: true },
  { key: "tailwinds", label: "Tailwinds", isText: true },
  {
    key: "whatIsInForecast",
    label: "What is in Forecast",
    isText: true,
  },
  {
    key: "varianceToBudget",
    label: "Variance to Budget",
    isText: true,
  },
  {
    key: "varianceToForecast",
    label: "Variance to Forecast",
    isText: true,
  },
  { key: "notes", label: "Notes", isText: true },
];

const ROWS_TAB2 = [
  {
    bu: "Federal Law Enforcement",
    forecastVersion: "Live EAC",
    projectNumber: "22402.001",
    pm: "WALKER, AMANDA",
    headwinds: "PM on leave through end of May; forecast updates delayed.",
    tailwinds:
      "Strong performance metrics expected to support follow-on award.",
    whatIsInForecast: "8 FTE labor through Q4 FY26; travel and ODCs included.",
    varianceToBudget: "$45,000",
    varianceToForecast: "$12,000",
    notes:
      "Option year exercise expected by July 2026. Awaiting CO confirmation.",
  },
  {
    bu: "Army",
    forecastVersion: "Live EAC",
    projectNumber: "22402.002",
    pm: "BROOKS, KEVIN",
    headwinds: "",
    tailwinds: "Option year exercise expected; additional headcount approved.",
    whatIsInForecast: "6 FTE through Q3; subcontract labor added in Q4.",
    varianceToBudget: "($18,500)",
    varianceToForecast: "$5,200",
    notes: "Subcontract negotiation finalized. Burn rate on track.",
  },
  {
    bu: "Dept of Homeland Security",
    forecastVersion: "Live EAC",
    projectNumber: "22402.003",
    pm: "FLORES, MARIA",
    headwinds:
      "Technical refresh delayed by supply chain issues; timeline at risk.",
    tailwinds: "",
    whatIsInForecast: "Hardware procurement and 4 FTE integration labor.",
    varianceToBudget: "($62,000)",
    varianceToForecast: "($8,400)",
    notes: "Escalated to PM council. Mitigation plan submitted to contracting.",
  },
  {
    bu: "Navy",
    forecastVersion: "Q2",
    projectNumber: "22402.004",
    pm: "HARRIS, MICHAEL",
    headwinds: "Contract modification still pending CO signature.",
    tailwinds: "New task order scope confirmed; revenue increase expected Q3.",
    whatIsInForecast: "3 FTE labor plus travel through end of base period.",
    varianceToBudget: "$9,800",
    varianceToForecast: "$2,100",
    notes:
      "Base period ends September 2026. Option year funding not yet confirmed.",
  },
  {
    bu: "Cyber",
    forecastVersion: "Q2",
    projectNumber: "22402.005",
    pm: "PATEL, PRIYA",
    headwinds: "",
    tailwinds: "Subcontract award increases labor coverage through year-end.",
    whatIsInForecast: "5 FTE cybersecurity labor; cloud infrastructure ODCs.",
    varianceToBudget: "$27,500",
    varianceToForecast: "$11,300",
    notes: "ATO received. System deployment on schedule for Q3.",
  },
  {
    bu: "Intelligence",
    forecastVersion: "Q2",
    projectNumber: "22402.006",
    pm: "NGUYEN, THOMAS",
    headwinds: "Two key staff departures impacting Q2 delivery.",
    tailwinds: "",
    whatIsInForecast: "4 FTE analytic support; no travel planned.",
    varianceToBudget: "($33,000)",
    varianceToForecast: "($7,500)",
    notes: "Backfill recruiting in progress. Expect 3–4 week gap in coverage.",
  },
];

function ContractNotes22402Tab({ filters, version }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  const [textFilters, setTextFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKeys, setSortKeys] = useState([]);

  useEffect(() => {
    setLoading(true);
    getContractNotes22402API(filters, version)
      .then((res) => {
        const apiRows = (res?.data?.rows || []).map(mapContractNotesRow);
        setRows(apiRows);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [JSON.stringify(filters), version]); // eslint-disable-line react-hooks/exhaustive-deps

  const visCols = COLS_TAB2.filter((c) => !hiddenCols.has(c.key));

  useEffect(() => {
    if (!openFilterKey && !colPickerOpen) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]")
      )
        setOpenFilterKey(null);
      if (colPickerRef.current && !colPickerRef.current.contains(e.target))
        setColPickerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey, colPickerOpen]);

  useEffect(() => {
    setPage(1);
  }, [textFilters]);

  const isTextActive = (key) => !!(textFilters[key] && textFilters[key].trim());

  const toggleFilter = useCallback(
    (key) => {
      if (openFilterKey === key) {
        setOpenFilterKey(null);
        return;
      }
      const btn = filterBtnRefs.current[key];
      if (btn)
        setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      setOpenFilterKey(key);
    },
    [openFilterKey],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = rows.filter((row) => {
    for (const col of COLS_TAB2) {
      if (HTML_NOTE_KEY_SET.has(col.key)) continue;
      const v = textFilters[col.key];
      if (
        v &&
        v.trim() &&
        !String(row[col.key] ?? "")
          .toLowerCase()
          .includes(v.trim().toLowerCase())
      )
        return false;
    }
    return true;
  });

  const getSortColumnLabel = useCallback(
    (key) => COLS_TAB2.find((c) => c.key === key)?.label || key,
    [],
  );
  const getSortValue = useCallback((row, key) => row[key] ?? "", []);
  const handleSort = useCallback((colKey) => {
    if (!SORTABLE_COL_KEYS.has(colKey)) return;
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);
  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const totalFiltered = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = sortedRows.slice(start, start + pageSize);
  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  if (loading) return <Loading />;

  return (
    <>
      <div style={CARD}>
        <div style={HEAD}>
          <h2 style={TITLE_S}>Contract Notes — Project 22402</h2>
          <div style={{ position: "relative" }} ref={colPickerRef}>
            <button
              type="button"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                height: 28,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #E5E7EB",
                background: colPickerOpen ? "#F0F4FF" : "#fff",
                color: "#374151",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: FONT,
              }}
              onClick={() => setColPickerOpen((p) => !p)}
            >
              <ColumnsIcon /> Columns
            </button>
            {colPickerOpen && (
              <div style={COL_PICKER_DROP}>
                {COLS_TAB2.map((col) => {
                  const hidden = hiddenCols.has(col.key);
                  return (
                    <label
                      key={col.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 14px",
                        cursor: "pointer",
                        fontSize: 12,
                        color: "#374151",
                        background: "none",
                        userSelect: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#F8F9FF";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "none";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!hidden}
                        style={{
                          accentColor: "#4F46E5",
                          width: 13,
                          height: 13,
                        }}
                        onChange={() =>
                          setHiddenCols((prev) => {
                            const next = new Set(prev);
                            if (hidden) next.delete(col.key);
                            else next.add(col.key);
                            return next;
                          })
                        }
                      />
                      {col.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={CARD_BODY}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                borderCollapse: "collapse",
                borderSpacing: 0,
                fontFamily: FONT,
                fontSize: 12,
                width: "max-content",
                minWidth: "100%",
                tableLayout: "fixed",
              }}
            >
              <thead>
                <tr style={{ height: 34 }}>
                  {visCols.map((col, ci) => {
                    const sortable = SORTABLE_COL_KEYS.has(col.key);
                    return (
                      <th
                        key={col.key}
                        style={{
                          ...thStyle(ci === visCols.length - 1, false),
                          ...columnSize(col, filteredRows),
                          ...(NOWRAP_COL_KEYS.has(col.key)
                            ? { whiteSpace: "nowrap" }
                            : null),
                          cursor: sortable ? "pointer" : undefined,
                        }}
                        onClick={sortable ? onThClick(col.key) : undefined}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            justifyContent: "space-between",
                          }}
                        >
                          {sortable ? (
                            <WaterfallThLabel
                              label={col.label}
                              colKey={col.key}
                              sortKeys={sortKeys}
                              getLabel={getSortColumnLabel}
                              highlight={openFilterKey === col.key}
                            />
                          ) : (
                            <span
                              style={{
                                color:
                                  openFilterKey === col.key
                                    ? "#60A5FA"
                                    : undefined,
                              }}
                            >
                              {col.label}
                            </span>
                          )}
                          {!HTML_NOTE_KEY_SET.has(col.key) && (
                            <button
                              ref={(el) => {
                                filterBtnRefs.current[col.key] = el;
                              }}
                              data-filter-btn
                              style={filterIconBtn(
                                isTextActive(col.key) ||
                                  openFilterKey === col.key,
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFilter(col.key);
                              }}
                              title="Filter"
                            >
                              <FilterFunnelIcon />
                            </button>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => {
                  const bg = i % 2 === 0 ? "#FFFFFF" : "#F7FAFC";
                  return (
                    <tr key={`${row.projectNumber}-${i}`}>
                      {visCols.map((col) => {
                        const v = row[col.key];
                        const isNoteCol = HTML_NOTE_KEY_SET.has(col.key);
                        return (
                          <td
                            key={col.key}
                            style={{
                              ...tdStyle(bg, false, false, false),
                              ...columnSize(col, filteredRows),
                              verticalAlign: isNoteCol ? "top" : "middle",
                              ...(isNoteCol
                                ? { overflow: "visible", whiteSpace: "normal" }
                                : NOWRAP_COL_KEYS.has(col.key)
                                  ? {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                    }
                                  : null),
                            }}
                          >
                            {col.key === "bu" ? (
                              <span
                                style={{ fontWeight: 600, color: "#111827" }}
                              >
                                {v}
                              </span>
                            ) : isNoteCol ? (
                              <div style={NOTE_CELL_STYLE}>{v}</div>
                            ) : (
                              v
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={visCols.length}
                      style={{
                        padding: "24px 20px",
                        textAlign: "center",
                        color: "#9CA3AF",
                        fontSize: 13,
                        fontFamily: FONT,
                      }}
                    >
                      No rows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={paginationBar}>
            <span style={paginationInfo}>
              {totalFiltered === 0
                ? "No rows match the current filters"
                : `${start + 1}–${Math.min(start + pageSize, totalFiltered)} of ${totalFiltered} rows`}
            </span>
            <div style={paginationRight}>
              <span style={pageSizeLabel}>Rows Per Page</span>
              <select
                style={pageSizeSelect}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <div style={paginationControls}>
                <button
                  style={pgBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage(1)}
                  title="First"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M9 10L6 7l3-3M5 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pgBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => p - 1)}
                  title="Previous"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M8.5 10.5L5 7l3.5-3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {pageNumbers.map((p, i) => (
                  <React.Fragment key={p}>
                    {i > 0 && pageNumbers[i - 1] < p - 1 && (
                      <span style={pgDots}>…</span>
                    )}
                    <button
                      style={p === safePage ? pgBtnActive : pgBtn(false)}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
                <button
                  style={pgBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  title="Next"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5.5 3.5L9 7l-3.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pgBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Last"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5 10l3-3-3-3M9 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{
              ...filterPanelStyle,
              top: filterPanelPos.top,
              left: filterPanelPos.left,
            }}
          >
            <div style={filterWrap}>
              <input
                autoFocus
                type="text"
                style={filterTextInput}
                placeholder="Search…"
                value={textFilters[openFilterKey] || ""}
                onChange={(e) =>
                  setTextFilters((p) => ({
                    ...p,
                    [openFilterKey]: e.target.value,
                  }))
                }
              />
              {textFilters[openFilterKey] && (
                <button
                  style={clearBtn}
                  onClick={() =>
                    setTextFilters((p) => {
                      const n = { ...p };
                      delete n[openFilterKey];
                      return n;
                    })
                  }
                >
                  ✕
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main export                                                   */
/* ══════════════════════════════════════════════════════════════ */
export default function ContractNotes() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();

  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [version, setVersion] = useState("");
  const [draftFilters, setDraftFilters] = useState(initFilterValues);

  const visibleKeys = TAB_FILTERS[activeTab] || null;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  /* Fetch versions when year changes; auto-select first */
  const currentYear =
    appliedFilters.year && String(appliedFilters.year) !== "All"
      ? String(appliedFilters.year)
      : String(new Date().getFullYear());

  useEffect(() => {
    getVersionsAPI(currentYear)
      .then((res) => {
        if (res?.data?.length) {
          const opts = res.data.map((v) => ({ value: v.key, label: v.name }));
          setVersion((prev) => prev || opts[0]?.value || "");
        }
      })
      .catch(() => {});
  }, [currentYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = (
    visibleKeys
      ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
      : FILTERS_CONFIG
  ).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
    setFilterOpen(false);
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const handleDownload = useCallback(async () => {
    const entry = DOWNLOAD_BY_TAB[activeTab];
    if (!entry || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await entry.api(effectiveFilters, version);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Contract Notes download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, version, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Contract Notes</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.push(`/projects/reports/contract_notes/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={
                    f.key === "year" &&
                    String(val) === String(new Date().getFullYear())
                  }
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Hero header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow="Contract & Budget"
          title="Contract Notes"
          accent={
            activeTab === "contract_notes_22402"
              ? "Project 22402 Notes"
              : "Project Notes & Comments"
          }
          badge="Live Forecast"
        />
      </div>

      {/* ── Body ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {activeTab === "contract_notes" && (
            <ContractNotesTab filters={effectiveFilters} version={version} />
          )}
          {activeTab === "contract_notes_22402" && (
            <ContractNotes22402Tab filters={effectiveFilters} version={version} />
          )}
        </div>
      </div>
    </div>
  );
}
